import { Space } from "antd";
import days from "dayjs";
import { isEmpty, sortBy, uniq, uniqBy } from "lodash-es";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { FilterTypes } from "../types";
import { mapMultiSelectOptions } from "../utilities";
import { type IMultipleSelectFilterOption, MultipleSelectFilter } from "./MultipleSelectFilter";

export interface IFixtureFiltersProperties {
  serverFixtures: any[];
  competitorType: CompetitorTypes;
  onFilterChange: (filterType: FilterTypes, values: string[]) => void;
  currentFilter: { [key: string]: string[] };
}

export const FixtureFilters = (properties: IFixtureFiltersProperties) => {
  const { formatMessage } = useIntl();

  const serverFixtures = properties.serverFixtures || [];

  const homeCompetitorOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.homeCompetitor?.entity) ? { entityId: "", nameFullLocal: "" } : x.homeCompetitor.entity)),
      (x) => x.entityId,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.entityId,
      (x) => x.nameFullLocal,
      (x) => properties.currentFilter.AwayCompetitors?.includes(x.entityId),
    );
  }, [properties, serverFixtures]);

  const awayCompetitorOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.awayCompetitor?.entity) ? { entityId: "", nameFullLocal: "" } : x.awayCompetitor.entity)),
      (x) => x.entityId,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.entityId,
      (x) => x.nameFullLocal,
      (x) => properties.currentFilter.HomeCompetitors?.includes(x.entityId),
    );
  }, [properties, serverFixtures]);

  const homeParticipantsOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.flatMap((x) => x.homeParticipants).map((x) => (isEmpty(x?.person) ? { personId: "", nameFullLocal: "" } : x.person)),
      (x) => x.personId,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.personId,
      (x) => x.nameFullLocal,
      (x) => properties.currentFilter.AwayParticipants?.includes(x.personId),
    );
  }, [properties, serverFixtures]);

  const awayParticipantsOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.flatMap((x) => x.awayParticipants).map((x) => (isEmpty(x?.person) ? { personId: "", nameFullLocal: "" } : x.person)),
      (x) => x.personId,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.personId,
      (x) => x.nameFullLocal,
      (x) => properties.currentFilter.HomeParticipants?.includes(x.personId),
    );
  }, [properties, serverFixtures]);

  const venuesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.venue) ? { venueId: "", nameLocal: "" } : x.venue)),
      (x) => x.venueId,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.venueId,
      (x) => x.nameLocal,
    );
  }, [serverFixtures]);

  const startDatesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = sortBy(
      uniq(serverFixtures.map((x) => (isEmpty(x.startTimeLocal) ? "" : (x.startTimeLocal.split("T")[0] ?? "")))),
      (x) => x && days(x),
    );
    return items.map((x) => ({
      label: isEmpty(x)
        ? ""
        : days().toDate() === days(x).toDate()
          ? formatMessage({ id: "fixtureListPage.filters.startDate.today" })
          : days(x).format("DD/MM/YYYY"),
      value: x,
    }));
  }, [formatMessage, serverFixtures]);

  const fixtureTypesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniq(serverFixtures.map((x) => (isEmpty(x.fixtureType) ? "" : x.fixtureType)));
    return mapMultiSelectOptions(
      items,
      (x) => x,
      (x) => x,
    );
  }, [serverFixtures]);

  const fixtureStatusesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniq(serverFixtures.map((x) => (isEmpty(x.status) ? "" : x.status)));
    return mapMultiSelectOptions(
      items,
      (x) => x,
      (x) => x,
    );
  }, [serverFixtures]);

  const stagesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.stage) ? { stageCode: "", nameLocal: "" } : x.stage)),
      (x) => x.stageCode,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.stageCode,
      (x) => x.nameLocal,
    );
  }, [serverFixtures]);

  const poolsOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.pool) ? { poolCode: "", nameLocal: "" } : x.pool)),
      (x) => x.poolCode,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.poolCode,
      (x) => x.nameLocal,
    );
  }, [serverFixtures]);

  const seriesOptions: IMultipleSelectFilterOption[] = useMemo(() => {
    const items = uniqBy(
      serverFixtures.map((x) => (isEmpty(x.series) ? { seriesCode: "", nameLocal: "" } : x.series)),
      (x) => x.seriesCode,
    );
    return mapMultiSelectOptions(
      items,
      (x) => x.seriesCode,
      (x) => x.nameLocal,
    );
  }, [serverFixtures]);

  return (
    <Space wrap className={"mb-3 fixtures-filters"}>
      {properties.competitorType === CompetitorTypes.Entity && (
        <>
          <MultipleSelectFilter
            filterType={FilterTypes.HomeCompetitors}
            placeholder={formatMessage({
              id: "fixtureListPage.filters.homeCompetitors.allOption",
            })}
            options={homeCompetitorOptions}
            onChange={properties.onFilterChange}
          />

          <MultipleSelectFilter
            filterType={FilterTypes.AwayCompetitors}
            placeholder={formatMessage({
              id: "fixtureListPage.filters.awayCompetitors.allOption",
            })}
            options={awayCompetitorOptions}
            onChange={properties.onFilterChange}
          />
        </>
      )}

      {properties.competitorType === CompetitorTypes.Person && (
        <>
          <MultipleSelectFilter
            filterType={FilterTypes.HomeParticipants}
            placeholder={formatMessage({
              id: "fixtureListPage.filters.homeParticipants.allOption",
            })}
            options={homeParticipantsOptions}
            onChange={properties.onFilterChange}
          />

          <MultipleSelectFilter
            filterType={FilterTypes.AwayParticipants}
            placeholder={formatMessage({
              id: "fixtureListPage.filters.awayParticipants.allOption",
            })}
            options={awayParticipantsOptions}
            onChange={properties.onFilterChange}
          />
        </>
      )}

      <MultipleSelectFilter
        filterType={FilterTypes.Venues}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.venues.allOption",
        })}
        options={venuesOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.StartDates}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.startDates.allOption",
        })}
        options={startDatesOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.FixtureTypes}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.fixtureTypes.allOption",
        })}
        options={fixtureTypesOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.FixtureStatuses}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.statuses.allOption",
        })}
        options={fixtureStatusesOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.Stages}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.stages.allOption",
        })}
        options={stagesOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.Pools}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.pools.allOption",
        })}
        options={poolsOptions}
        onChange={properties.onFilterChange}
      />

      <MultipleSelectFilter
        filterType={FilterTypes.Series}
        placeholder={formatMessage({
          id: "fixtureListPage.filters.series.allOption",
        })}
        options={seriesOptions}
        onChange={properties.onFilterChange}
      />
    </Space>
  );
};
